import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Upload, RotateCcw, Download, ShieldCheck, Film, MessageSquare, Clock, AlertTriangle, ExternalLink, X } from "lucide-react";
import { getOrder, deliverOrder, approveOrder, reviseOrder, startConversation, postReview, uploadDelivery, getOrderSla, refundOrder, formatApiError, updateOrderSoftware, previewUrl, downloadUrl, fetchGatedBlob, acceptOrder, declineOrder } from "../lib/api";
import StatusPill from "../components/StatusPill";
import InfoTip from "../components/InfoTip";
import SoftwareAccessCard from "../components/SoftwareAccessCard";
import VerifiedBadge from "../components/VerifiedBadge";
import { STATUS_LABELS, STATUS_TIMELINE, COMMISSION_RATE, timeAgo } from "../lib/constants";
import { useUser } from "../context/UserContext";
import { toast } from "sonner";

/** Render a <video> fed by a gated endpoint. Fetches bytes through axios
 *  (injects Authorization header) and wraps them in a blob URL. Falls back
 *  to "preparing…" while loading. */
function GatedVideo({ orderId, kind, className, ...rest }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let created = null;
    setErr(null);
    setBlobUrl(null);
    fetchGatedBlob(orderId, kind)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        created = u;
        setBlobUrl(u);
      })
      .catch((e) => {
        if (!cancelled) setErr(formatApiError(e, "Video unavailable"));
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [orderId, kind]);
  if (err) return <div className={`w-full h-full flex items-center justify-center text-xs text-[#ff8888] ${className || ""}`}>{err}</div>;
  if (!blobUrl) return <div className={`w-full h-full flex items-center justify-center text-xs text-[#606070] ${className || ""}`}>Preparing video…</div>;
  return <video src={blobUrl} className={className} {...rest} />;
}

/** Trigger an authenticated download via a disposable <a download>. */
async function gatedDownload(orderId, kind, filename) {
  const url = await fetchGatedBlob(orderId, kind);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `${orderId}.mp4`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}



export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, openChat } = useUser();
  const [order, setOrder] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeliver, setShowDeliver] = useState(false);
  const [showRevise, setShowRevise] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [showSoftwareDialog, setShowSoftwareDialog] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const load = () => {
    setLoading(true);
    getOrder(id)
      .then(async (o) => {
        setOrder(o);
        try { setSla(await getOrderSla(id)); } catch (_) { setSla(null); }
      })
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  const isCompany = user?.role === "company" && order && order.company_id === user.id;
  const isCreator = user?.role === "creator" && order && order.creator_id === user.id;

  const onApprove = async () => {
    try {
      const u = await approveOrder(id); setOrder(u);
      toast.success("Approved · funds released to creator");
      setShowReview(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onAccept = async () => {
    try {
      const u = await acceptOrder(id);
      setOrder(u);
      toast.success("Order accepted · you can start working");
    } catch (e) {
      toast.error(formatApiError(e, "Could not accept order."));
    }
  };

  const onDecline = async () => {
    if (!window.confirm("Decline this order? The buyer will be refunded in LTC and the order will be cancelled.")) return;
    try {
      const u = await declineOrder(id);
      setOrder(u);
      toast.success("Order declined · refund queued for the buyer");
    } catch (e) {
      toast.error(formatApiError(e, "Could not decline order."));
    }
  };

  const onRefund = async () => {
    if (!window.confirm("Refund this order? The creator missed their max delivery time and your full payment will be returned.")) return;
    setRefunding(true);
    try {
      const u = await refundOrder(id);
      setOrder(u);
      toast.success("Refund issued · $" + (u.price || order.price) + " returned to you");
    } catch (e) {
      toast.error(formatApiError(e, "Could not issue refund."));
    } finally {
      setRefunding(false);
    }
  };

  const onOpenChat = async () => {
    if (!isCompany) return;
    try {
      const conv = await startConversation(order.creator_id);
      openChat(conv);
    } catch { toast.error("Could not open chat"); }
  };

  if (loading) return <div className="max-w-5xl mx-auto px-6 py-16 text-[#606070]">Loading…</div>;
  if (!order) return <div className="max-w-5xl mx-auto px-6 py-16 text-white">Order not found.</div>;

  // New pricing model: `order.price` = what the creator earns.
  // `order.platform_fee` = 10% paid by the buyer on TOP. Fallback for legacy
  // orders that were created before the fee was split out.
  const platformFee = typeof order.platform_fee === "number"
    ? order.platform_fee
    : Math.round(order.price * COMMISSION_RATE);
  const buyerTotal = typeof order.buyer_total === "number"
    ? order.buyer_total
    : order.price + platformFee;
  const creatorEarns = order.price;

  return (
    <div data-testid="order-detail-page" className="max-w-6xl mx-auto px-6 py-10">
      <button onClick={() => nav(-1)} data-testid="order-back-btn" className="text-sm text-[#a0a0b0] hover:text-white flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="font-mono text-2xl md:text-3xl text-[#00ff88]">{order.id}</span>
        <StatusPill status={order.status} />
      </div>
      <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white mb-1">
        {order.content_type} · {order.game}
      </h1>
      <div className="text-sm text-[#a0a0b0] flex items-center gap-1.5 flex-wrap">
        <span>{order.company_name} → {order.creator_name}</span>
        <VerifiedBadge verified={order.creator_verified} testIdSuffix="order" size="xs" />
      </div>

      {/* Awaiting-acceptance banner shown to the buyer */}
      {isCompany && order.status === "paid" && (
        <AwaitingAcceptanceBanner order={order} />
      )}

      {/* Refund-pending banner after a creator declines */}
      {order.refund_pending && order.status === "cancelled" && (
        <div
          data-testid="order-refund-pending-banner"
          className="mt-6 rounded-xl border border-[#ff4444]/30 bg-[#ff4444]/5 px-4 py-3 text-sm text-[#ffcccc] flex items-start gap-2"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <strong>Order declined by the creator.</strong> Your refund of $
            {order.buyer_total ?? order.price} is being returned to your LTC wallet — usually within 24h.
          </span>
        </div>
      )}

      {/* Progress bar */}
      <div className="mt-8 cc-surface rounded-2xl p-6">
        <div className="cc-overline mb-4">Progress</div>
        <ProgressBar status={order.status} />
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <div className="md:col-span-2 space-y-6">
          <Section title="Brief">
            <p className="text-sm text-[#a0a0b0] leading-relaxed whitespace-pre-wrap">{order.brief || "—"}</p>
            {order.revision_feedback && order.status === "revision_requested" && (
              <div className="mt-4 rounded-lg border border-[#ff4444]/30 bg-[#ff4444]/5 p-3 text-sm text-[#ffcccc]">
                <div className="cc-overline text-[#ff4444] mb-1">Revision feedback</div>
                {order.revision_feedback}
              </div>
            )}
          </Section>

          <Section title="Delivery">
            {!(order.deliverable_url || order.preview_url) && (
              <div className="text-xs text-[#606070]">No delivery uploaded yet.</div>
            )}
            {order.status === "delivered" && (() => {
              // Always stream through the gated preview endpoint when the order
              // has a watermarked copy. Fall back to legacy URL fields for old
              // orders that were created before the refactor.
              const hasGated = order.has_watermarked === true;
              const legacyUrl = order.preview_url || order.deliverable_url || "";
              const legacyAbs = legacyUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${legacyUrl}` : legacyUrl;
              const isDirectVideo = hasGated || /\.(mp4|webm|mov|mkv|avi)(\?|$)/i.test(legacyUrl);
              const isWatermarked = hasGated || (order.preview_url && order.preview_url !== order.deliverable_url);
              const src = hasGated ? previewUrl(order.id) : legacyAbs;
              if (!hasGated && !legacyUrl) return null;
              return (
                <>
                  <div className="aspect-video rounded-xl overflow-hidden bg-[#06060c] border border-[#15152a]" data-testid="watermark-preview">
                    {isDirectVideo && hasGated ? (
                      <GatedVideo
                        orderId={order.id}
                        kind="preview"
                        data-testid="watermark-video"
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                        className="w-full h-full object-contain bg-black"
                      />
                    ) : isDirectVideo ? (
                      <video
                        data-testid="watermark-video"
                        src={src}
                        controls
                        controlsList="nodownload"
                        onContextMenu={(e) => e.preventDefault()}
                        className="w-full h-full object-contain bg-black"
                      />
                    ) : (
                      <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#00ff88] hover:underline"
                      >
                        <Film size={36} />
                        <span className="text-[11px] font-mono">Open preview ↗</span>
                      </a>
                    )}
                  </div>
                  <div className={`flex items-center gap-2 mt-3 text-[11px] ${isWatermarked ? "text-[#ffaa00]" : "text-[#606070]"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isWatermarked ? "bg-[#ffaa00]" : "bg-[#606070]"}`} />
                    {isWatermarked
                      ? "Watermarked preview · clean file unlocks on approval"
                      : "Preview · delivered as an external link (no watermark applied)"}
                  </div>
                  {hasGated && (
                    <div
                      data-testid="watermark-preview-note"
                      className="mt-2 rounded-md border border-[#15152a] bg-[#06060c] px-3 py-2 text-[11px] text-[#a0a0b0] leading-relaxed"
                    >
                      Preview is shown in 720p for fast playback. The full-quality file will be available after approval.
                    </div>
                  )}
                </>
              );
            })()}
            {order.status === "completed" && (order.has_watermarked || order.deliverable_url) && (
              <>
                <div className="aspect-video rounded-xl overflow-hidden bg-[#06060c] border border-[#15152a]">
                  {order.has_watermarked ? (
                    <GatedVideo
                      orderId={order.id}
                      kind="download"
                      controls
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                    <video
                      src={order.deliverable_url}
                      controls
                      className="w-full h-full object-contain bg-black"
                    />
                  )}
                </div>
                {order.has_watermarked ? (
                  <button
                    type="button"
                    data-testid="order-deliverable-link"
                    onClick={async () => {
                      try { await gatedDownload(order.id, "download", `${order.id}.mp4`); }
                      catch (e) { toast.error(formatApiError(e, "Download failed")); }
                    }}
                    className="inline-flex items-center gap-2 text-sm text-[#00ff88] hover:underline font-mono mt-3"
                  >
                    <Download size={14} /> Download clean file
                  </button>
                ) : (
                  <a
                    href={order.deliverable_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="order-deliverable-link"
                    className="inline-flex items-center gap-2 text-sm text-[#00ff88] hover:underline font-mono mt-3"
                  >
                    <Download size={14} /> Download clean file
                  </a>
                )}
              </>
            )}
            {(order.deliverable_url || order.preview_url) && order.status === "revision_requested" && (
              <div className="text-xs text-[#ffaa00]">Creator is preparing a revised delivery.</div>
            )}
          </Section>

          {(order.software || (user?.role === "company" && user?.id === order.company_id)) && (
            <SoftwareAccessCard
              order={order}
              viewerRole={user?.role}
              onUpdateClick={user?.role === "company" && user?.id === order.company_id ? () => setShowSoftwareDialog(true) : undefined}
            />
          )}

          <Section title="Timeline">
            <ol className="relative border-l border-[#15152a] ml-2 space-y-4 pl-6">
              {(order.history || []).map((h, i) => (
                <li key={i} data-testid={`timeline-${i}`} className="relative">
                  <span className="absolute -left-[25px] top-1 w-2 h-2 rounded-full bg-[#00ff88]" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill status={h.status} />
                    <span className="text-[11px] text-[#606070]">{timeAgo(h.at)}</span>
                  </div>
                  {h.note && <div className="text-sm text-[#a0a0b0] mt-1">{h.note}</div>}
                </li>
              ))}
            </ol>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Escrow & Payment">
            <Row label="Creator earns" value={<span className="font-mono text-[#00ff88]">${creatorEarns}</span>} />
            <Row label={`Platform fee (${Math.round(COMMISSION_RATE * 100)}%)`} value={<span className="font-mono">${platformFee}</span>} />
            <Row label="Total paid by buyer" value={<span className="font-mono text-white font-semibold">${buyerTotal}</span>} />
            <Row label="Deadline" value={<span className="font-mono">{order.deadline?.slice(0, 10)}</span>} />
            <Row
              label="Payment method"
              value={
                order.payment_method === "ltc" ? (
                  <span data-testid="order-payment-badge-ltc" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/30">
                    ⚡ LTC
                  </span>
                ) : (
                  <span data-testid="order-payment-badge-card" className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest bg-[#0d0d1a] text-[#a0a0b0] border border-[#15152a]">
                    💳 Card
                  </span>
                )
              }
            />
            {sla && (
              <Row
                label={
                  <span className="inline-flex items-center gap-1.5">
                    Max delivery time
                    <InfoTip
                      testId="sla-info-tip"
                      text={`The creator has committed to deliver within ${sla.max_delivery_days} day${sla.max_delivery_days > 1 ? "s" : ""} of order placement. If they miss this window, you can request a full refund of $${order.price} from this page — no questions asked.`}
                    />
                  </span>
                }
                value={<span className={`font-mono ${sla.exceeded ? "text-[#ff8888]" : ""}`}>{sla.max_delivery_days}d{sla.exceeded ? " · exceeded" : ""}</span>}
              />
            )}

            {/* LTC awaiting payment CTA */}
            {order.payment_method === "ltc" && order.status === "awaiting_payment" && (
              <div data-testid="order-pay-ltc-cta" className="mt-3 p-3 rounded-lg border border-[#ffaa00]/30 bg-[#ffaa00]/5 flex items-start gap-2">
                <Clock size={14} className="text-[#ffaa00] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xs text-[#ffd566] font-medium">Waiting for your LTC payment</div>
                  <div className="text-[11px] text-[#a0a0b0] mt-0.5">Send the exact LTC amount to the deposit address.</div>
                </div>
                <button
                  data-testid="order-go-to-payment"
                  onClick={() => nav(`/pay/${order.id}`)}
                  className="cc-btn-primary rounded-md px-3 py-1.5 text-[11px]"
                >
                  Open payment
                </button>
              </div>
            )}

            {/* LTC tx hash */}
            {order.ltc?.tx_hash && (
              <a
                data-testid="order-ltc-txhash"
                href={`https://blockchair.com/litecoin/transaction/${order.ltc.tx_hash}`}
                target="_blank" rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#00ff88] hover:underline font-mono break-all"
              >
                tx · {order.ltc.tx_hash.slice(0, 16)}…<ExternalLink size={10} />
              </a>
            )}

            {/* LTC release info */}
            {order.payment_method === "ltc" && order.ltc_release && (
              <div data-testid="order-ltc-release" className="mt-3 p-3 rounded-lg border border-[#15152a] bg-[#06060c]">
                <div className="text-[10px] uppercase tracking-widest text-[#606070] mb-1">LTC payout</div>
                <div className="text-xs text-white font-mono">{order.ltc_release.net_ltc} LTC</div>
                <div className="text-[11px] text-[#a0a0b0] mt-0.5">Status: {order.ltc_release.status}</div>
                {order.ltc_release.tx_hash && (
                  <a href={`https://blockchair.com/litecoin/transaction/${order.ltc_release.tx_hash}`} target="_blank" rel="noreferrer" className="text-[11px] text-[#00ff88] hover:underline font-mono break-all">
                    {order.ltc_release.tx_hash}
                  </a>
                )}
                {order.ltc_release.error && (
                  <div className="text-[11px] text-[#ff8888] mt-1">{order.ltc_release.error}</div>
                )}
              </div>
            )}

            <div className="mt-3 p-3 rounded-lg border border-[#00ff88]/20 bg-[#00ff88]/5 flex items-start gap-2">
              <ShieldCheck size={14} className="text-[#00ff88] mt-0.5" />
              <div className="text-[11px] text-[#a0a0b0] leading-relaxed">
                {order.status === "completed"
                  ? "Funds have been released to the creator."
                  : order.status === "cancelled" && order.refunded
                    ? "This order was refunded — full payment returned to you."
                    : "Funds are held in escrow and released when you approve delivery. If the creator misses their max delivery time, you get a full refund."}
              </div>
            </div>
          </Section>

          <Section title="Actions">
            <div className="space-y-2">
              {/* Creator must explicitly accept a paid order before delivery. */}
              {isCreator && order.status === "paid" && (
                <>
                  <button
                    data-testid="order-accept-btn"
                    onClick={onAccept}
                    className="rounded-lg px-4 py-2.5 text-sm w-full inline-flex items-center justify-center gap-2 bg-[#00ff88] hover:bg-[#00e67a] text-[#06060c] font-semibold shadow-[0_0_18px_-4px_rgba(0,255,136,0.65)]"
                  >
                    <Check size={14} /> Accept the order
                  </button>
                  <button
                    data-testid="order-decline-btn"
                    onClick={onDecline}
                    className="rounded-lg px-4 py-2.5 text-sm w-full inline-flex items-center justify-center gap-2 bg-[#ff4444] hover:bg-[#ff2d2d] text-white font-semibold shadow-[0_0_18px_-4px_rgba(255,68,68,0.55)]"
                  >
                    <X size={14} /> Decline the order
                  </button>
                  <div className="text-[10px] text-[#606070] text-center mt-1">
                    Decline triggers an automatic LTC refund to the buyer.
                  </div>
                </>
              )}

              {/* Creator actions */}
              {isCreator && (order.status === "in_progress" || order.status === "revision_requested") && (
                <button
                  data-testid="order-deliver-btn"
                  onClick={() => setShowDeliver(true)}
                  className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm w-full inline-flex items-center justify-center gap-2"
                >
                  <Upload size={14} /> Upload Delivery
                </button>
              )}

              {/* Company actions on delivered */}
              {isCompany && order.status === "delivered" && (
                <>
                  <button
                    data-testid="order-approve-btn"
                    onClick={onApprove}
                    className="cc-btn-primary rounded-lg px-4 py-2.5 text-sm w-full inline-flex items-center justify-center gap-2"
                  >
                    <Check size={14} /> Approve & Release
                  </button>
                  <button
                    data-testid="order-revise-btn"
                    onClick={() => setShowRevise(true)}
                    className="rounded-lg px-4 py-2.5 text-sm w-full border border-[#ff4444]/30 bg-[#ff4444]/5 text-[#ffcccc] hover:bg-[#ff4444]/10 inline-flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={14} /> Request Revision
                  </button>
                  <div className="text-[10px] text-[#606070] text-center mt-1">
                    Revisions: {order.revision_count || 0} / 2
                  </div>
                </>
              )}

              {/* Refund button when SLA exceeded */}
              {isCompany && sla?.refundable && (
                <button
                  data-testid="order-refund-btn"
                  onClick={onRefund}
                  disabled={refunding}
                  className="rounded-lg px-4 py-2.5 text-sm w-full border border-[#ffaa00]/40 bg-[#ffaa00]/10 text-[#ffd566] hover:bg-[#ffaa00]/20 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <AlertTriangle size={14} /> {refunding ? "Refunding…" : `Claim full refund ($${order.price})`}
                </button>
              )}

              {isCompany && (
                <button
                  onClick={onOpenChat}
                  data-testid="order-open-chat-btn"
                  className="rounded-lg px-4 py-2.5 text-sm w-full border border-[#15152a] bg-[#0d0d1a] hover:border-[#1f1f3a] text-white inline-flex items-center justify-center gap-2"
                >
                  <MessageSquare size={14} /> Open chat
                </button>
              )}

              {order.status === "completed" && (
                <div className="text-center text-xs text-[#00ff88] font-medium">Order completed ✓</div>
              )}
            </div>
          </Section>
        </div>
      </div>

      {showDeliver && (
        <DeliverDialog
          order={order}
          onClose={() => setShowDeliver(false)}
          onDelivered={(u) => { setOrder(u); setShowDeliver(false); toast.success("Delivery uploaded (watermarked preview created)"); }}
        />
      )}
      {showRevise && (
        <ReviseDialog
          order={order}
          onClose={() => setShowRevise(false)}
          onRevised={(u) => { setOrder(u); setShowRevise(false); toast.success("Revision requested"); }}
        />
      )}
      {showReview && (
        <ReviewDialog
          order={order}
          onClose={() => setShowReview(false)}
          onDone={() => { setShowReview(false); toast.success("Review posted · thanks!"); }}
        />
      )}
      {showSoftwareDialog && (
        <SoftwareDialog
          order={order}
          onClose={() => setShowSoftwareDialog(false)}
          onSaved={(u) => { setOrder(u); setShowSoftwareDialog(false); toast.success("Software access shared with the creator"); }}
        />
      )}
    </div>
  );
}

const ProgressBar = ({ status }) => {
  const isRevision = status === "revision_requested";
  const idx = Math.max(0, STATUS_TIMELINE.indexOf(isRevision ? "in_progress" : status));
  return (
    <div className="flex items-center gap-2">
      {STATUS_TIMELINE.map((s, i) => {
        const active = i <= idx && !isRevision;
        const current = i === idx && !isRevision;
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-[11px] transition-colors ${
                active
                  ? (s === "completed" ? "bg-[#00ff88] text-[#06060c]" : "bg-[#00ff88]/20 text-[#00ff88] border border-[#00ff88]/40")
                  : "bg-[#0a0a14] text-[#606070] border border-[#15152a]"
              }`}>
                {i + 1}
              </div>
              <div className={`text-[10px] uppercase tracking-widest text-center ${current ? "text-white" : "text-[#606070]"}`}>
                {STATUS_LABELS[s]}
              </div>
            </div>
            {i < STATUS_TIMELINE.length - 1 && (
              <div className={`h-px flex-1 mb-6 ${i < idx ? "bg-[#00ff88]/40" : "bg-[#15152a]"}`} />
            )}
          </React.Fragment>
        );
      })}
      {isRevision && (
        <div className="ml-3 text-[10px] text-[#ff4444] bg-[#ff4444]/10 border border-[#ff4444]/30 rounded-full px-2 py-1 uppercase tracking-widest">
          Revision
        </div>
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="cc-surface rounded-2xl p-6">
    <div className="cc-overline mb-4">{title}</div>
    {children}
  </section>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-[#15152a] last:border-0">
    <span className="text-[11px] text-[#606070] uppercase tracking-widest">{label}</span>
    <span className="text-sm text-white">{value}</span>
  </div>
);

const DeliverDialog = ({ order, onClose, onDelivered }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [mode, setMode] = useState("file"); // file | url

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) {
      toast.error("File too large (max 500 MB).");
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (mode === "file") {
        if (!file) { toast.error("Pick a video file first."); setSaving(false); return; }
        // Upload goes directly to the order-scoped gated endpoint. The server
        // now runs FFmpeg in a BackgroundTask so the POST returns once the
        // bytes are on disk. We then poll GET /orders/{id} every 3s until
        // `processing` flips back to false and status becomes `delivered`.
        const resp = await uploadDelivery(order.id, file, (p) => {
          setProgress(p);
          if (p >= 100) setProcessing(true);
        });
        if (resp && resp.processing) setProcessing(true);
        const deadline = Date.now() + 10 * 60 * 1000;
        let fresh = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            fresh = await getOrder(order.id);
          } catch (_) { continue; }
          if (fresh?.processing_error) {
            throw new Error(fresh.processing_error);
          }
          if (fresh && !fresh.processing && fresh.status === "delivered") {
            break;
          }
        }
        if (!fresh || fresh.processing || fresh.status !== "delivered") {
          throw new Error("Video is still processing. Refresh the page in a minute.");
        }
        onDelivered(fresh);
      } else {
        if (!externalUrl.trim()) { toast.error("Paste a video URL."); setSaving(false); return; }
        const u = await deliverOrder(order.id, externalUrl.trim(), externalUrl.trim());
        onDelivered(u);
      }
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    } finally {
      setSaving(false);
      setProcessing(false);
    }
  };

  return (
    <div data-testid="deliver-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-md">
        <div className="cc-overline">Upload delivery</div>
        <h3 className="text-xl font-semibold text-white mt-1 mb-4">{order.id}</h3>

        <div className="flex gap-1 mb-4 p-1 rounded-lg bg-[#06060c] border border-[#15152a]">
          <button
            type="button"
            data-testid="deliver-mode-file"
            onClick={() => setMode("file")}
            className={`flex-1 py-1.5 text-xs rounded-md transition ${mode === "file" ? "bg-[#00ff88]/10 text-[#00ff88]" : "text-[#a0a0b0] hover:text-white"}`}
          >
            Upload .mp4
          </button>
          <button
            type="button"
            data-testid="deliver-mode-url"
            onClick={() => setMode("url")}
            className={`flex-1 py-1.5 text-xs rounded-md transition ${mode === "url" ? "bg-[#00ff88]/10 text-[#00ff88]" : "text-[#a0a0b0] hover:text-white"}`}
          >
            Paste URL
          </button>
        </div>

        {mode === "file" ? (
          <div>
            <input
              ref={fileRef}
              data-testid="deliver-file-input"
              type="file"
              accept="video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv,.avi"
              onChange={onPick}
              className="hidden"
            />
            <button
              type="button"
              data-testid="deliver-pick-file-btn"
              onClick={() => fileRef.current?.click()}
              className="w-full p-5 rounded-lg border-2 border-dashed border-[#15152a] hover:border-[#00ff88]/40 bg-[#06060c] text-center transition-colors"
            >
              <Upload size={20} className="mx-auto text-[#00ff88] mb-2" />
              <div className="text-sm text-white">{file ? file.name : "Click to choose a video file"}</div>
              <div className="text-[11px] text-[#606070] mt-1">
                {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : "MP4, MOV, WebM, MKV — max 500 MB"}
              </div>
            </button>
            {saving && progress > 0 && (
              <div className="mt-3">
                <div className="h-1.5 bg-[#15152a] rounded-full overflow-hidden">
                  <div className="h-full bg-[#00ff88] transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="text-[11px] text-[#606070] mt-1 text-right">{progress}%</div>
              </div>
            )}
            {processing && (
              <div data-testid="deliver-processing" className="mt-3 p-3 rounded-lg border border-[#00ff88]/30 bg-[#00ff88]/5 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-[#00ff88] border-t-transparent rounded-full animate-spin" />
                <div className="flex-1">
                  <div className="text-sm text-[#00ff88]">Processing video…</div>
                  <div className="text-[11px] text-[#a0a0b0]">Burning watermark · 1–5 min depending on file size. Please don't close this window.</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="cc-overline mb-1">Video URL (YouTube / direct link)</div>
            <input
              data-testid="deliver-url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=…"
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
            />
          </div>
        )}

        <div className="text-[11px] text-[#606070] mt-3">
          A watermarked preview is shown to the company. The clean file is released once approved.
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button
            data-testid="deliver-submit"
            onClick={submit}
            disabled={saving || (mode === "file" ? !file : !externalUrl.trim())}
            className="cc-btn-primary rounded-lg px-5 py-2 text-sm disabled:opacity-50"
          >
            {saving ? "Uploading…" : "Submit Delivery"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviseDialog = ({ order, onClose, onRevised }) => {
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!feedback.trim()) return;
    setSaving(true);
    try {
      const u = await reviseOrder(order.id, feedback.trim());
      onRevised(u);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="revise-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-md">
        <div className="cc-overline">Request revision</div>
        <h3 className="text-xl font-semibold text-white mt-1 mb-4">{order.id}</h3>
        <div className="cc-overline mb-1">Feedback for the creator</div>
        <textarea
          data-testid="revise-feedback"
          rows={4}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What needs to change?"
          className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] cc-focus-ring"
        />
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button data-testid="revise-submit" onClick={submit} disabled={saving || !feedback.trim()} className="cc-btn-primary rounded-lg px-5 py-2 text-sm disabled:opacity-50">
            {saving ? "Sending…" : "Send Revision Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReviewDialog = ({ order, onClose, onDone }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await postReview({ creator_id: order.creator_id, order_id: order.id, rating, comment });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="review-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-md">
        <div className="cc-overline">Leave a review</div>
        <h3 className="text-xl font-semibold text-white mt-1 mb-1 flex items-center gap-1.5 flex-wrap">
          <span>How was {order.creator_name}?</span>
          <VerifiedBadge verified={order.creator_verified} testIdSuffix="review" size="xs" showInfo={false} />
        </h3>
        <p className="text-xs text-[#a0a0b0] mb-5">Your review helps other companies pick the right creator.</p>

        <div className="flex items-center justify-center gap-1 mb-5" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((v) => {
            const active = (hover || rating) >= v;
            return (
              <button
                key={v}
                type="button"
                data-testid={`review-star-${v}`}
                onMouseEnter={() => setHover(v)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(v)}
                className={`text-3xl transition-colors ${active ? "text-[#ffaa00]" : "text-[#303050]"}`}
                aria-label={`${v} stars`}
              >★</button>
            );
          })}
        </div>

        <textarea
          data-testid="review-comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional — tell companies what stood out."
          className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] cc-focus-ring"
        />

        <div className="flex items-center justify-between gap-2 mt-6">
          <button data-testid="review-skip" onClick={onClose} className="px-4 py-2 text-sm text-[#606070] hover:text-white">
            Skip
          </button>
          <button
            data-testid="review-submit"
            onClick={submit}
            disabled={saving}
            className="cc-btn-primary rounded-lg px-6 py-2.5 text-sm disabled:opacity-50"
          >
            {saving ? "Posting…" : "Submit Review"}
          </button>
        </div>
      </div>
    </div>
  );
};

import SoftwareAccessSection from "../components/SoftwareAccessSection";

const SoftwareDialog = ({ order, onClose, onSaved }) => {
  const initial = order?.software || { method: "later", url: "", license_key: "", license_duration: "", instructions: "" };
  const [value, setValue] = useState({
    method: initial.method || "later",
    url: initial.url || "",
    license_key: initial.license_key || "",
    license_duration: initial.license_duration || "",
    instructions: initial.instructions || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const updated = await updateOrderSoftware(order.id, value);
      onSaved(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="software-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-2xl my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="cc-overline">Share software access</div>
            <h3 className="text-xl font-semibold text-white mt-1">{order.id}</h3>
          </div>
          <button onClick={onClose} className="text-[#606070] hover:text-white" data-testid="software-dialog-close">✕</button>
        </div>
        <SoftwareAccessSection value={value} onChange={setValue} />
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button
            data-testid="software-dialog-save"
            onClick={submit}
            disabled={saving}
            className="cc-btn-primary rounded-lg px-5 py-2 text-sm disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save software access"}
          </button>
        </div>
      </div>
    </div>
  );
};


// ─── Acceptance countdown banner ────────────────────────────────────
function AwaitingAcceptanceBanner({ order }) {
  const [timeoutHours, setTimeoutHours] = useState(48);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // Fetch the configured timeout (falls back to 48h if the endpoint fails).
    fetch("/api/stats")
      .then((r) => r.json())
      .then((d) => {
        const h = Number(d?.order_accept_timeout_hours);
        if (h > 0) setTimeoutHours(h);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const paidAtIso =
    (order.history || []).find((h) => h.status === "paid")?.at ||
    order.updated_at ||
    order.created_at;
  const paidAt = paidAtIso ? new Date(paidAtIso).getTime() : null;
  const deadline = paidAt ? paidAt + timeoutHours * 3_600_000 : null;
  const remainingMs = deadline ? Math.max(0, deadline - now) : null;

  let label = `Creator has ${timeoutHours}h to accept · otherwise auto-refunded`;
  if (remainingMs != null) {
    const h = Math.floor(remainingMs / 3_600_000);
    const m = Math.floor((remainingMs % 3_600_000) / 60000);
    label =
      remainingMs > 0
        ? `${h}h ${m}m before auto-refund`
        : "Auto-refund pending — running in the next sweep";
  }

  return (
    <div
      data-testid="order-awaiting-acceptance-banner"
      className="mt-6 rounded-xl border border-[#ffaa00]/30 bg-[#ffaa00]/5 px-4 py-3 text-sm text-[#ffd566] flex items-center justify-between gap-3 flex-wrap"
    >
      <span className="flex items-center gap-2">
        <Clock size={14} /> Waiting for {order.creator_name} to accept your order.
      </span>
      <span
        data-testid="order-acceptance-countdown"
        className="font-mono text-xs text-[#ffaa00] bg-[#0a0a14] border border-[#ffaa00]/20 rounded px-2 py-1"
      >
        ⏳ {label}
      </span>
    </div>
  );
}

